import {
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { CollaborationService } from '@observation/shared/services/collaboration.service';
import { APP_CONFIG, ConfigSettings } from '@shared/config';
import { ShareMode } from '@shared/config/navigation.config';
import { Contact } from '@shared/models';
import {
  CONTACT_TYPES_NAMES,
  ContactType,
} from '@shared/models/collaboration.model';
import {
  GraphResponseStatus,
  MESSAGE_GRAPH_API_STATUS,
} from '@shared/models/graph-api.model';
import { validateEmailList } from '@shared/value-objects/mail-list-validator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'od-collaboration-input',
  templateUrl: './collaboration-input.component.html',
  styleUrls: ['./collaboration-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CollaborationInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CollaborationInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CollaborationInputComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() baseContacts: Contact[] = [];
  @Input() contactGroups: Contact[] = [];
  @Output() valueChanged: EventEmitter<Contact[]> = new EventEmitter();

  public search = new Subject<string>();
  public search$ = this.search.asObservable();

  public searchContacts: Contact[][] = [];
  public showContacts: boolean = false;
  public contacts: Contact[] = [];

  public collaborationInput: UntypedFormControl;

  public showGraphApiContacts: boolean = false;
  public isContactsEmpty: boolean = false;

  public listStatus: GraphResponseStatus;
  public GraphResponseStatus = GraphResponseStatus;
  public MESSAGE_GRAPH_API_STATUS = MESSAGE_GRAPH_API_STATUS;
  public CONTACT_TYPES_NAMES = CONTACT_TYPES_NAMES;

  private subscriptions = new Subject<void>();

  constructor(
    @Inject(APP_CONFIG) settings: ConfigSettings,
    public collaborationService: CollaborationService
  ) {
    this.showGraphApiContacts = settings.useOauth;
    this.collaborationInput = new UntypedFormControl(
      '',
      this.collaborationService.shareMode === ShareMode.Email
        ? [validateEmailList]
        : null
    );
  }

  ngOnInit(): void {
    if (this.showGraphApiContacts) {
      this.collaborationService
        .getSearchStream(this.search$)
        .pipe(takeUntil(this.subscriptions))
        .subscribe(result => {
          this.searchContacts = [
            [...this.baseContacts, ...this.contactGroups],
            ...result.value,
          ];
          this.isContactsEmpty = !result.value.find(res => res.length);
          this.listStatus = result.status;
        });
    } else {
      console.warn(
        'The Microsoft Graph API Contacts cannot be loaded because of OAuth configuration.'
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.unsubscribe();
  }

  public makeSearch(search: string): void {
    this.search.next(search);
  }

  validate(): ValidationErrors | null {
    return this.collaborationInput.errors;
  }

  onChange(contacts: Contact[]) {
    this.contacts = contacts;
  }

  addContact(contact: Contact) {
    const newContacts =
      contact.type === ContactType.EMAIL_GROUP && contact?.contacts.length
        ? contact.contacts
        : [contact];

    newContacts.forEach(newContact => {
      if (!this.contacts.find(c => c.id === newContact.id)) {
        this.contacts = [...this.contacts, newContact];
      }
    });

    this.hideContactsPanel();
    this.writeValue(this.contacts);
  }

  removeContact(contact: Contact): void {
    const contactIndex = this.contacts.findIndex(
      addedContact => addedContact.id === contact.id
    );
    this.contacts.splice(contactIndex, 1);
    this.writeValue(this.contacts);
  }

  onTouched = () => {};

  /**
   * Allows Angular to update the model.
   * Update the model and changes needed for the view here.
   */
  writeValue(contacts: Contact[]): void {
    this.onChange(contacts);
    this.valueChanged.emit(contacts);
  }

  /**
   * Allows Angular to register a function to call when the model changes.
   * Save the function as a property to call later here.
   */
  registerOnChange(fn: (contacts: Contact[]) => void): void {
    this.onChange = fn;
  }

  /**
   * Allows Angular to register a function to call when the input has been touched.
   * Save the function as a property to call later here.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  togglePanel() {
    this.showContactsPanel();
  }

  showContactsPanel() {
    this.showContacts = true;
  }

  hideContactsPanel() {
    this.showContacts = false;
    this.collaborationInput.setValue('');
    this.makeSearch('');
  }

  checkEmail(event) {
    if (this.collaborationService.shareMode === ShareMode.Email) {
      if (event.key !== 'Tab') {
        event.preventDefault();
      }

      if (!this.collaborationInput.value || this.collaborationInput.errors) {
        this.writeValue(this.contacts);
        return;
      }

      const contacts = this.getEmailsFromText(this.collaborationInput.value);
      contacts.forEach(contact => this.addContact(contact));
      this.hideContactsPanel();
    }
  }

  private getEmailsFromText(text: string = ''): Contact[] {
    const splitByCommaWhitespaceSemicolonRegex = /[^\(\s,;)]+/g;
    const contacts = text.match(splitByCommaWhitespaceSemicolonRegex) || [];
    const contactsArray: Contact[] = contacts.map(mail => ({
      id: mail,
      displayName: mail,
      mail,
      type: ContactType.EMAIL,
    }));
    return contactsArray;
  }
}
